import React, { useRef } from 'react';
import * as meta from '../../../meta-data.json';
import { handleKeyDown } from './keyEvents';
import { useTree } from './reducer';
import { baseClassNames, clickActions, treeTypes, TreeviewProps } from './types';
import { onComponentBlur } from './utils';
import { Node } from './viewNode';

export const TreeView = React.forwardRef(function TreeView(
	{
		data,
		nodeRenderer,
		onSelect,
		onExpand,
		className = '',
		multiSelect = false,
		propagateSelect = false,
		propagateSelectUpwards = false,
		propagateCollapse = false,
		expandOnKeyboardSelect = false,
		togglableSelect = false,
		defaultExpandedIds = [],
		defaultSelectedIds = [],
		defaultDisabledIds = [],
		clickAction,
		onBlur,
		size,
		...other
	}: TreeviewProps,
	ref,
) {
	const nodeRefs = useRef({});
	const [state, dispatch]: any = useTree({
		data,
		defaultExpandedIds,
		defaultSelectedIds,
		defaultDisabledIds,
		nodeRefs,
		onSelect,
		onExpand,
		multiSelect,
		propagateSelect,
		propagateSelectUpwards,
	});
	propagateSelect = propagateSelect && multiSelect;

	let innerRef: any = useRef();
	if (ref != null) innerRef = ref;

	return (
		<ul
			className={baseClassNames.root + ' ' + className}
			style={{ margin: '0', padding: '0' }}
			role="tree"
			data-epo={`epo-treeview-${meta.version}`}
			{...{ part: `epo-treeview-${meta.version}` }}
			aria-multiselectable={multiSelect}
			ref={innerRef}
			onBlur={event => {
				onComponentBlur(event, innerRef.current, () => {
					onBlur &&
						onBlur({
							treeState: state,
							dispatch,
						});
					dispatch({ type: treeTypes.blur });
				});
			}}
			onKeyDown={handleKeyDown({
				data,
				tabbableId: state.tabbableId,
				expandedIds: state.expandedIds,
				selectedIds: state.selectedIds,
				disabledIds: state.disabledIds,
				halfSelectedIds: state.halfSelectedIds,
				dispatch,
				propagateCollapse,
				propagateSelect,
				multiSelect,
				expandOnKeyboardSelect,
				togglableSelect,
			})}
			{...other}
		>
			{data[0].children.map((x: any, index: number) => (
				<Node
					key={x}
					data={data}
					element={data[x]}
					setsize={data[0].children.length}
					posinset={index + 1}
					level={1}
					{...state}
					state={state}
					dispatch={dispatch}
					nodeRefs={nodeRefs}
					baseClassNames={baseClassNames}
					nodeRenderer={nodeRenderer}
					propagateCollapse={propagateCollapse}
					propagateSelect={propagateSelect}
					propagateSelectUpwards={propagateSelectUpwards}
					multiSelect={multiSelect}
					togglableSelect={togglableSelect}
					clickAction={clickAction || clickActions.select}
					size={size}
				/>
			))}
		</ul>
	);
});
